import "leaflet/dist/leaflet.css";

import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import L from "leaflet";
import {Map, TileLayer} from "react-leaflet"; 
import RouteMapPoints from "../RouteMapPoints";
import MapRoute from "../MapRoute";
import MapControls from "../MapControls";
import MapRestrictPolygons from "../MapRestrictPolygons";

import {isEqual} from "../../bl/routeService";
import styles from "./Map.scss";
import getMapBounds, {getVisitedRoutePoints} from "./Map.bl";
import MIN_ZOOM from "../../constants/zoomValues";
import {
  createDefaultRouteRequestFromPoint,
  createDefaultRouteRequestThroughPoint,
  createDefaultRouteRequestToPoint,
  fillRouteRequestPointFromGeoCodedObject
} from "../../bl/routeRequestUtils";
import {batch} from "react-redux";
import api from "../../api";
import {headerBottomHeight} from "../../utils";
import {withNamespaces} from "react-i18next";

const MapDefaultPadding = {
  leftWithSidebar: 345,
  left: 60,
  right: 60,
  top: 32,
  bottom: 32,
};

const PhoneVersionWidth = 559;

class AtiMap extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      position: undefined,
      zoom: 13,
      isMobileVersion: this.isPhoneDimensions(),
    };
    
    const southWest = L.latLng(-85, Number.NEGATIVE_INFINITY);
    const northEast = L.latLng(85, Number.POSITIVE_INFINITY);
    this.maxBounds = L.latLngBounds(southWest, northEast);
    this.resizeTimeout = null;
    this.minZoom = this.getCurrentMinZoom();
    this.maxZoom = 18; // максимальный уровень, на который сгенерены тайлы.
    this.map = React.createRef();
    this.contextMenuRef = React.createRef();
    this.contextMenuItemFromRef = React.createRef();
    this.contextMenuItemToRef = React.createRef();
    this.contextMenuItemPassthroughRef = React.createRef();
  }

  static defaultPosition = [59.939738, 30.334514];
  componentDidMount() {
    const { props } = this;
    const { showLightVersion, getVisualRouteInBounds } = props;
    
    this.mapPadding = this.getMapPadding();

    if (showLightVersion) {
      props.toggleSidebar();
    }

    const leafletMap = this.map.current.leafletElement;
    
    leafletMap.on("zoomend", () => {
      const zoom = leafletMap.getZoom();
      const bounds = leafletMap.getBounds();
      this.setState({ zoom });
      getVisualRouteInBounds(bounds);
    });
    
    leafletMap.on("dragend", () => {
      const bounds = leafletMap.getBounds();
      getVisualRouteInBounds(bounds);
    });
    window.addEventListener("resize", this.resizeThrottler);
    window.addEventListener("click", this.onMapClick);
    if (!showLightVersion) {
      leafletMap.addEventListener("contextmenu", this.onContextMenu);
    }
    
    this.contextMenuItemFromRef.current.addEventListener("click", this.handleRouteFromSelect);
    this.contextMenuItemToRef.current.addEventListener("click", this.handleRouteToSelect);
    this.contextMenuItemPassthroughRef.current.addEventListener("click", this.handleRoutePassthroughSelect);
  }

  componentWillUnmount() {
    const { showLightVersion } = this.props;
    window.removeEventListener("resize", this.resizeThrottler);
    window.removeEventListener("click", this.onMapClick);
    if (!showLightVersion) {
      window.removeEventListener("contextmenu", this.onContextMenu);
    }

    this.contextMenuItemFromRef.current.removeEventListener("click", this.handleRouteFromSelect);
    this.contextMenuItemToRef.current.removeEventListener("click", this.handleRouteToSelect);
    this.contextMenuItemPassthroughRef.current.removeEventListener("click", this.handleRoutePassthroughSelect);
  }
  
  componentDidUpdate(prevProps, prevState, snapshot) {
    const { routeInfo } = this.props;
    const { position } = this.state;
    
    if((!position || prevProps.routeInfo !== routeInfo) && routeInfo.segments.length > 0){
      // рассчитываем центр карты с учетом точек маршрута и смещения относительно боковой панели.
      const leafletElement = this.map.current.leafletElement;
      const geoPoints = this.getGeoPointsFromRouteInfo(routeInfo);
      const routeBounds = getMapBounds(geoPoints);
      leafletElement.fitBounds(routeBounds, this.mapPadding);
      const center = leafletElement.getCenter();
      this.setState({position: center});
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const { routeInfo, visualRoute, routeNotFound, sidebarToggled, routeSegments } = this.props;

    const { state } = this;

    const zoomChanged = state.zoom !== nextState.zoom;
    const versionChanged = nextState.isMobileVersion !== state.isMobileVersion;
    const positionChanged = nextState.position !== state.position;

    this.versionChanged = versionChanged;

    const routeStatusChanged = nextProps.routeNotFound !== routeNotFound ||
      (nextProps.sidebarToggled !== sidebarToggled && nextState.isMobileVersion);

    const routeSegmentsChanged = !isEqual(nextProps.routeSegments, routeSegments);
    
    if (zoomChanged || versionChanged || routeStatusChanged || positionChanged || routeSegmentsChanged) {
      return true;
    }

    const routeInfoEquals = isEqual(nextProps.routeInfo, routeInfo);
    if(!routeInfoEquals) {
      return true;
    }
    return !isEqual(nextProps.visualRoute, visualRoute);
  }

  getGeoPointsFromRouteInfo = routeInfo => {
    return routeInfo.segments.map(s => s.routePoint.geoPoint);
  };
    
  getMapPadding = () => {
    const { sidebarToggled, showLightVersion } = this.props;
    let paddingLeft = showLightVersion ? MapDefaultPadding.left : MapDefaultPadding.leftWithSidebar;
    let paddingBottom = MapDefaultPadding.bottom;
    let paddingTop = MapDefaultPadding.top;
    let paddingRight = MapDefaultPadding.right;
    const mapVisiblePart = L.Browser.mobile ? 320 : 300;

    if (this.isPhoneDimensions()) {
      paddingLeft = 30;
      paddingTop = 110;
      paddingBottom = 100;
      if (!sidebarToggled) {
        paddingRight = 30;
        const height = L.Browser.mobile ? window.screen.height : window.innerHeight;
        paddingBottom = height - mapVisiblePart;
      }
    }
    return {
      paddingTopLeft: [paddingLeft, paddingTop],
      paddingBottomRight: [paddingRight, paddingBottom],
    };
  };

  getCurrentMinZoom = () => MIN_ZOOM;

  actualResizeHandler = () => {
    this.setState({ isMobileVersion: this.isPhoneDimensions() });
  };

  isPhoneDimensions = () => document.documentElement.clientWidth < PhoneVersionWidth;

  showContextMenu = (show = true) => {
    this.contextMenuRef.current.style.display = show ? "block" : "none";
  };
  
  onContextMenu = e => {
    this.contextMenuMouseEvent = e;
    e.originalEvent.preventDefault();

    this.showContextMenu();

    const clickPoint = {x: e.originalEvent.x, y: e.originalEvent.y};
    const cm = this.contextMenuRef.current;
    cm.style.top = `${clickPoint.y + cm.offsetHeight > window.innerHeight
        ? window.innerHeight - cm.offsetHeight
        : clickPoint.y}px`;
    cm.style.left =
        `${clickPoint.x + cm.offsetWidth > window.innerWidth
            ? window.innerWidth - cm.offsetWidth
            : clickPoint.x}px`;
  };
  
  onMapClick = () => {
    this.showContextMenu(false);
    this.contextMenuMouseEvent = undefined;
  };

  handleRoutePointSelect = async (routePartName, latLng) => {
    const geoPoint = {lat: latLng.lat, lon: latLng.lng};

    const geoCodedObject = (await api.geo.reverseGeocoding([geoPoint]))[0];

    const routeRequest = this.props.routeRequest;
    let newRouteRequest;
    switch (routePartName) {
      case 'from':
        const fromRoutePoint = createDefaultRouteRequestFromPoint();
        await fillRouteRequestPointFromGeoCodedObject(fromRoutePoint, geoCodedObject);
        newRouteRequest = {...routeRequest, from: fromRoutePoint};
        break;
      case 'through':
        const throughRoutePoint = createDefaultRouteRequestThroughPoint();
        await fillRouteRequestPointFromGeoCodedObject(throughRoutePoint, geoCodedObject);
        
        const newThrough = [...routeRequest.through, throughRoutePoint];
        newRouteRequest = {...routeRequest, through: newThrough};
        break;
      case 'to':
        const toRoutePoint = createDefaultRouteRequestToPoint();
        await fillRouteRequestPointFromGeoCodedObject(toRoutePoint, geoCodedObject);
        newRouteRequest = {...routeRequest, to: toRoutePoint};
        break;
      default:
        console.error('wrong routePartName: ', routePartName);
        return;
    }

    batch(() => {
      this.props.updateRouteRequest(newRouteRequest);
      this.props.calculateRoute();
    });
  };
  
  handleRouteFromSelect = async () => {
    const latLng = this.contextMenuMouseEvent.latlng;
    await this.handleRoutePointSelect('from', latLng);
  };

  handleRouteToSelect = async () => {
    const latLng = this.contextMenuMouseEvent.latlng;
    await this.handleRoutePointSelect('to', latLng);
  };

  handleRoutePassthroughSelect = async () => {
    const latLng = this.contextMenuMouseEvent.latlng;
    await this.handleRoutePointSelect('through', latLng);
  };
  
  resizeThrottler = () => {
    if (!this.resizeTimeout) {
      this.resizeTimeout = setTimeout(() => {
        this.resizeTimeout = null;
        this.actualResizeHandler();
      }, 66);
    }
  };

  handleReturnToRoute = () => {
    const { props } = this;
    const geoPoints = this.getGeoPointsFromRouteInfo(props.routeInfo);
    const bounds = getMapBounds(geoPoints);
    if (bounds) {
      this.map.current.leafletElement.fitBounds(bounds, this.mapPadding);
    }
  };

  handleZoomIn = () => {
    this.map.current.leafletElement.zoomIn();
  };

  handleZoomOut = () => {
    this.map.current.leafletElement.zoomOut();
  };

  toggleSidebar = e => {
    const { props } = this;
    const { showLightVersion } = props;

    if (!e.originalEvent.defaultPrevented && !showLightVersion) {
      props.toggleSidebar();
    }
  };

  render() {
    const { isWelcomeScreen, 
      /** @type {RouteSegment[]} */
      routeSegments, 
      visualRoute, debugInfo, routeNotFound, sidebarToggled, showLightVersion, platonRate,t } = this.props;
    const visitedRoutePoints =  getVisitedRoutePoints(routeSegments);
    const { position, zoom, isMobileVersion } = this.state;
    
    
    const showRoute = visualRoute.segments.length > 0;

    const blurMap = isWelcomeScreen && !showLightVersion;

    const headerHeight = headerBottomHeight();
    const size = `calc(100vh - ${headerHeight}px)`;

    const showZoomButtons = !blurMap && ((sidebarToggled && isMobileVersion) || !isMobileVersion);
    
    if (this.map && !!this.versionChanged) {
      this.map.current.leafletElement.options.minZoom = this.getCurrentMinZoom();
    }
    
    const stickyStyle = showLightVersion ? styles.lightVersionSticky : styles.sticky;
    const mapControlsBottom = 29;

    const url = "/gw/gis-tiles/public/v1/tile/{z}/{x}/{y}.png";
    
    return (
        <React.Fragment>
          <div className={L.Browser.mobile ? styles.mobileSticky : stickyStyle}>
            <Map
                fadeAnimation={false} // щоб нативная анимация не мелькала при закрытии попапов и тултипов
                center={position || AtiMap.defaultPosition}
                zoom={zoom}
                zoomControl={false}
                style={{height: size, width: "100%", position: "absolute"}}
                className={classnames(styles.map, {
                  [styles.disabledMap]: blurMap,
                })}
                ref={this.map}
                minZoom={this.minZoom}
                maxZoom={this.maxZoom}
                zoomSnap={0.5}
                zoomDelta={0.5}
                wheelPxPerZoomLevel={70}
                boundsOptions={this.mapPadding}
                onclick={this.toggleSidebar}
                maxBoundsViscosity={1.0}
                worldCopyJump
                maxBounds={this.maxBounds}
                scrollWheelZoom={!blurMap}
                inertia={false}
            >
              <TileLayer
                  attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                  url={url}
              />
              {showRoute && <MapRoute visualRoute={visualRoute} platonRate={platonRate} map={this.map?.current.leafletElement}/>}
              <RouteMapPoints visitedRoutePoints={visitedRoutePoints} routeNotFound={routeNotFound}/>
              {showZoomButtons && (
                  <div className={styles.buttonPanel} style={{ bottom: headerHeight + mapControlsBottom }}>
                    <MapControls
                        onZoomIn={this.handleZoomIn}
                        onZoomOut={this.handleZoomOut}
                        onReturnToRoute={this.handleReturnToRoute}
                    />
                  </div>
              )}
              {debugInfo?.restrictPolygons && (
                <MapRestrictPolygons restrictPolygons={debugInfo.restrictPolygons} />
              )}
            </Map>
            {blurMap && <div className={styles.disabledLayer}/>}
          </div>
          <div className={styles.customContextMenu} style={{ display: "none" }} ref={this.contextMenuRef}>
            <div className={styles.customContextMenu__item} id="menu-item-route-from" ref={this.contextMenuItemFromRef}>{t("trace-map-menu-item-route-from","Маршрут отсюда")}</div>
            <div className={styles.customContextMenu__item} id="menu-item-route-to" ref={this.contextMenuItemToRef}>{t("trace-map-menu-item-route-to","Маршрут сюда")}</div>
            <div className={styles.customContextMenu__item} id="menu-item-route-through" ref={this.contextMenuItemPassthroughRef}>{t("trace-map-menu-item-route-through","Ехать через")}</div>
          </div>
        </React.Fragment>
    );
  }
}

AtiMap.defaultProps = {
  sidebarToggled: false,
  showLightVersion: false,
};

AtiMap.propTypes = {
  isWelcomeScreen: PropTypes.bool.isRequired,
  routeNotFound: PropTypes.bool.isRequired,
  sidebarToggled: PropTypes.bool,
  showLightVersion: PropTypes.bool,
  routeInfo: PropTypes.object,
  routeRequest: PropTypes.object.isRequired,
  visualRoute: PropTypes.object,
  platonRate: PropTypes.number,
  toggleSidebar: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

export default withNamespaces()(AtiMap);